import { Card, Checkbox, FormGroup } from "@cofano/cck";
import FormField from "components/cck/FormField";
import {
	Control,
	Controller,
	FormState,
	useFieldArray,
} from "react-hook-form";
import { Box, Grid2 as Grid } from "@mui/material";
import { Language } from "utils";
import ContainerTypeSelect from "view/containertypes/components/selects/ContainerTypeSelect";
import ContainerAmountSummary from "view/containers/components/summary/ContainerAmountSummary";
import { intl } from "LocaleWrapper";
import LocationSelect from "view/locations/components/selects/LocationSelect";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteIcon from "@mui/icons-material/Delete";
import { CallType } from "__generated__/graphql";
import CallTypeSelect from "components/selects/CallTypeSelect";
import { getCallTypeColor } from "utils/cargoActionUtils";
import { ExpectedCargo } from "view/cargos/types/cargo";
import NumberInput from "components/cck/NumberInput";
import "../../../voyages/components/form/styling.scss";
import { VoyageAddDialogSchemaInput, VoyageSchemaInput } from "view/voyages/utils/voyage.schema";

export type ExpectedCargosFormType = VoyageSchemaInput | { expectedCargos: ExpectedCargo[] } | VoyageAddDialogSchemaInput;

type ExpectedCargosFormProps = {
	control: Control<VoyageSchemaInput>,
	formState: FormState<VoyageSchemaInput>,
	nameBase: `calls.${number}.expectedCargos`,
	expectedCargos: ExpectedCargo[],
} | {
	control: Control<{ expectedCargos: ExpectedCargo[] }>,
	formState: FormState<{ expectedCargos: ExpectedCargo[] }>,
	nameBase: "expectedCargos",
	expectedCargos: ExpectedCargo[],
} | {
	control: Control<VoyageAddDialogSchemaInput>,
	formState: FormState<VoyageAddDialogSchemaInput>,
	nameBase: `calls.${number}.expectedCargos`,
	expectedCargos: ExpectedCargo[],
};

const ExpectedCargosForm = ({ control, formState, expectedCargos, nameBase }: ExpectedCargosFormProps) => {

	const { fields, append, remove } = useFieldArray<ExpectedCargosFormType>({
		control: control as Control<ExpectedCargosFormType>,
		name: nameBase,
	});

	const totals = expectedCargos.reduce(
		(acc, cargo) => {
			const amount = Number(cargo.amount ?? 0);
			if (cargo.actionType === CallType.Loading) {
				acc.loadingAmount += amount;
				acc.loadingTeu += Number(cargo.containerType?.teu ?? 0) * amount;
			} else if (cargo.actionType === CallType.Discharging) {
				acc.dischargingAmount += amount;
				acc.dischargingTeu += Number(cargo.containerType?.teu ?? 0) * amount;
			}
			return acc;
		},
		{ loadingAmount: 0, dischargingAmount: 0, loadingTeu: 0, dischargingTeu: 0 },
	);

	const amountDifference = totals.loadingAmount - totals.dischargingAmount;
	const teuDifference = totals.loadingTeu - totals.dischargingTeu;

	return (
		<div className="voyage-expected-cargos-form">
			<Card
				title={(
					<ContainerAmountSummary
						loadingAmount={totals.loadingAmount}
						dischargingAmount={totals.dischargingAmount}
						loadingTeu={totals.loadingTeu}
						dischargingTeu={totals.dischargingTeu}
						amountDifference={amountDifference}
						teuDifference={teuDifference}
						borderless
					/>
				)}
				actions={[ {
					icon: <AddRoundedIcon />,
					onClick: () => append({
						id: null,
						amount: null,
						actionType: CallType.Loading,
						dischargingLocation: null,
						containerType: null,
						full: false,
					}),
				} ]}
				disablePadding
			>
				<Box sx={{ padding: 2, backgroundColor: "#F0F0F0" }}>
					{fields.map((field, index) => (
						<ExpectedCargoRow
							key={field.id}
							nameBase={nameBase}
							expectedCargo={expectedCargos[index]}
							index={index}
							control={control as Control<ExpectedCargosFormType>}
							formState={formState}
							remove={remove}
						/>
					))}
				</Box>
			</Card>
		</div>
	);
};

const ExpectedCargoRow = ({ nameBase, expectedCargo, index, control, formState, remove }: {
	expectedCargo: ExpectedCargosFormProps["expectedCargos"][number] | undefined,
	index: number,
	control: Control<ExpectedCargosFormType>
} & Pick<ExpectedCargosFormProps, "formState" | "nameBase"> & Pick<ReturnType<typeof useFieldArray>, "remove">) => {

	let title;
	if (expectedCargo?.containerType) {
		title = expectedCargo.containerType.isoTypeCode;
		if (expectedCargo.amount) {
			title += ` (${expectedCargo.amount})`;
		}
	} else {
		title = intl.formatMessage({ id: "PRODUCTS_CONTAINER" }) + " " + (index + 1);
	}

	const isLoading = expectedCargo?.actionType === CallType.Loading;

	const regularFieldSize: Parameters<typeof Grid>[0]["size"] = { xs: 12, sm: isLoading ? 4 : 6, lg: isLoading ? 2 : 3 };

	return (
		<Box sx={{ marginBottom: 2 }}>
			<Card
				title={title}
				color={expectedCargo?.actionType ? getCallTypeColor(expectedCargo.actionType) : undefined}
				actions={[ {
					onClick: () => remove(index),
					icon: <DeleteIcon />,
				} ]}
				collapsable
			>
				<Grid columnSpacing={3} rowSpacing={1} container>
					<Grid size={regularFieldSize}>
						<FormGroup label={Language("TYPE")} overflow="hidden" required>
							<FormField errors={formState.errors} name={`${nameBase}.${index}.actionType`}>
								<Controller
									name={`${nameBase}.${index}.actionType`}
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<CallTypeSelect {...field} />
									)}
								/>
							</FormField>
						</FormGroup>
					</Grid>

					{isLoading && (
						<Grid size={{ xs: 12, sm: 8, lg: 4 }}>
							<FormGroup
								label={Language("ORDER_DISCHARGING_PLACE")}
								overflow="hidden"
								required
							>
								<FormField errors={formState.errors} name={`${nameBase}.${index}.dischargingLocation`}>
									<Controller
										name={`${nameBase}.${index}.dischargingLocation`}
										control={control}
										rules={{ required: true }}
										render={({ field }) => (
											<LocationSelect {...field} />
										)}
									/>
								</FormField>
							</FormGroup>
						</Grid>
					)}

					<Grid size={regularFieldSize}>
						<FormGroup label={Language("AMOUNT")} overflow="hidden" required>
							<FormField errors={formState.errors} name={`${nameBase}.${index}.amount`}>
								<Controller
									name={`${nameBase}.${index}.amount`}
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<NumberInput {...field} decimalScale={0} />
									)}
								/>
							</FormField>
						</FormGroup>
					</Grid>

					<Grid size={regularFieldSize}>
						<FormGroup label={Language("CARGO_CONTAINER_TYPE")} overflow="hidden" required>
							<FormField errors={formState.errors} name={`${nameBase}.${index}.containerType`}>
								<Controller
									name={`${nameBase}.${index}.containerType`}
									control={control}
									rules={{ required: true }}
									render={({ field }) => (
										<ContainerTypeSelect {...field} includeTeu />
									)}
								/>
							</FormField>
						</FormGroup>
					</Grid>

					<Grid size={regularFieldSize}>
						<FormGroup label={Language("FULL")}>
							<Box sx={{ marginTop: 1 }}>
								<FormField errors={formState.errors} name={`${nameBase}.${index}.full`}>
									<Controller
										name={`${nameBase}.${index}.full`}
										control={control}
										render={({ field }) => (
											<Checkbox
												{...field}
												value={undefined}
												checked={field.value}
											/>
										)}
									/>
								</FormField>
							</Box>
						</FormGroup>
					</Grid>
				</Grid>
			</Card>
		</Box>
	);
};

export default ExpectedCargosForm;
